import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Tabs, TabList, TabPanels, Tab, TabPanel} from '@chakra-ui/react'

import {useSiteContext} from '../../../../../contexts/site'
import TabItem from './tab-item'
import {useShopperContext, useUsid} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useCustomerGroups from '../../../../../hooks/use-customer-groups'

const Categories = ({
    categories,
    productShowName,
    productShowPrice,
    productShowColorSwatches,
    analyticsProps = {},
    showMetalLabel = false
}) => {
    const {isYR} = useSiteContext()
    const {data: customerGroupIds} = useCustomerGroups()
    const borderColor = isYR ? 'black' : 'yellow.600'
    const {
        app: {commerceAPI}
    } = getConfig()
    const {bloomreach} = commerceAPI
    const {config} = bloomreach || {}

    // Parse Bloomreach configuration safely
    let enableBloomreachSearch = false,
        catalogViews = '',
        accountId = ''
    try {
        const configAsJson = JSON.parse(config)
        if (configAsJson) {
            enableBloomreachSearch = configAsJson.enableBloomreachSearch
            accountId = configAsJson.accountId
            catalogViews = configAsJson.catalogViews
        }
    } catch (e) {
        console.error(e.message)
    }

    const {site} = useMultiSite()
    const {usid} = useUsid()

    const [isViewed, setIsViewed] = useState(false)
    const categoryTabs = useRef(null)
    useEffect(() => {
        // If the category component is not yet viewed - just redner 5 tiles rather than 15.
        const target = categoryTabs?.current
        if (!target || isViewed) return

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsViewed(true)
                observer.disconnect()
            }
        })

        observer.observe(target)

        return () => observer.disconnect()
    }, [isViewed])

    // Fetch shopper context if Bloomreach search is enabled and USID is available
    const {data: shopperContext} = useShopperContext(
        {
            parameters: {
                usid: usid || localStorage.getItem('pdUsid'),
                siteId: site.id
            }
        },
        {
            enabled:
                !isServer &&
                enableBloomreachSearch &&
                (Boolean(usid) || Boolean(localStorage.getItem('pdUsid')))
        }
    )

    return (
        <Tabs isLazy ref={categoryTabs}>
            {/* Tab headers */}
            <TabList
                width={{
                    base: 'calc(100% - 30px)',
                    lg: 'calc(100% - 40px)',
                    '2xl': 'calc(100% - 80px)'
                }}
                marginX={{base: '15px', lg: '20px', '2xl': '40px'}}
                borderBottom="4px solid"
                borderColor="gray.350"
            >
                {categories.map(
                    (category, categoryIndex) =>
                        category.title && (
                            <Tab
                                key={categoryIndex}
                                flex="1"
                                paddingTop={{base: '10px', lg: '20px'}}
                                paddingBottom={{base: '16px', lg: '10px'}}
                                paddingX="10px"
                                outline="4px solid transparent"
                                borderBottom="4px solid"
                                marginBottom="-4px"
                                color="black.100"
                                borderColor="gray.350"
                                justifyContent={{
                                    base: category.mobileAlignment,
                                    lg: category.desktopAlignment
                                }}
                                _selected={{
                                    borderColor: borderColor,
                                    color: 'black.100'
                                }}
                            >
                                <Text
                                    as="span"
                                    fontFamily={category.fontFamily || 'inter'}
                                    fontSize="16px"
                                    fontWeight="400"
                                    lineHeight="25px"
                                    textAlign={{
                                        base: category.mobileAlignment,
                                        lg: category.desktopAlignment
                                    }}
                                    letterSpacing="0.08px"
                                >
                                    {category.title}
                                </Text>
                            </Tab>
                        )
                )}
            </TabList>

            {/* Tab panels */}
            <TabPanels>
                {categories.map(
                    (category) =>
                        (category.id || category.productsList) &&
                        category.title && (
                            <TabPanel key={category.id} padding="0">
                                <TabItem
                                    id={category.id}
                                    title={category.title}
                                    productsList={category.productsList}
                                    isViewed={isViewed}
                                    customerGroupIds={customerGroupIds}
                                    effectiveDateTime={shopperContext?.effectiveDateTime}
                                    categoryFilters={category.categoryFilters}
                                    enableBloomreachSearch={enableBloomreachSearch}
                                    accountId={accountId}
                                    catalogViews={catalogViews}
                                    productShowName={productShowName}
                                    productShowPrice={productShowPrice}
                                    productShowColorSwatches={productShowColorSwatches}
                                    analyticsProps={analyticsProps}
                                    showMetalLabel={showMetalLabel}
                                    lifeStyle={category.lifeStyle}
                                />
                            </TabPanel>
                        )
                )}
            </TabPanels>
        </Tabs>
    )
}

Categories.propTypes = {
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            fontFamily: PropTypes.string,
            id: PropTypes.string
        })
    ),
    productShowName: PropTypes.bool,
    productShowPrice: PropTypes.bool,
    productShowColorSwatches: PropTypes.bool,
    analyticsProps: PropTypes.object,
    showMetalLabel: PropTypes.bool
}

export default Categories
